import * as d3 from 'd3';

import each from 'lodash/each';

import last from 'lodash/last';
import first from 'lodash/first';

import React from 'react';
import PropTypes from 'prop-types';


const margin = {
  top: 40,
  left: 70,
  right: 40,
  bottom: 40
};

class LineChartComponent extends React.Component {
  constructor (props) {
    super(props);
    this.createLineChart = () => {
      const width = this.props.canvasWidth - margin.left - margin.right;
      const height = this.props.canvasHeight - margin.top - margin.bottom;

      const { ranges, graphTicks, drawZAxis, isInvalidForm } = this.props;

      const x = d3
        .scaleLinear()
        .range([0, width])
        .domain([ranges.x.min, ranges.x.max]);

      const y = d3
        .scaleLinear()
        .range([height, 0])
        .domain([ranges.y.min, ranges.y.max]);

      const line = d3
        .line()
        .x(d => x(d.x))
        .y(d => y(d.y))
        .curve(d3.curveMonotoneX);

      const svg = d3.select(this.node).select('#container').attr(
        'transform', `translate(${margin.left}, ${margin.top})`
      );

      let formatter = d3.axisBottom(x);
      if (graphTicks !== null) {
        formatter = d3.axisBottom(x).tickFormat(d3.format('.2f')).tickValues(graphTicks);
      }

      // Draw y-Axis with y-scale
      svg
        .select('#y-axis')
        .attr('class', 'y axis')
        .call(d3.axisLeft(y));

      // Draw x-Axis with x-scale
      svg
        .select('#x-axis')
        .attr('class', 'x axis')
        .attr('transform', `translate(0, ${height})`)
        .call(formatter);

      // remove old label on x-Axis, y-Axis, and z-Axis
      svg.selectAll('.y-label').remove();
      svg.selectAll('.x-label').remove();
      svg.selectAll('.z-label').remove();

      // Draw y-Axis label
      svg.append('text')
        .attr('class', 'y-label')
        .attr('transform', 'rotate(0)')
        .attr('y', 0 - margin.left + 30)
        .attr('dy', '1em')
        .style('text-anchor', 'middle')
        .text('f(x)');

      // Draw x-Axis label
      svg.append('text')
        .attr('class', 'x-label')
        .attr('transform', `translate( ${(width + 30)} , ${(height)})`)
        .style('text-anchor', 'end')
        .text('x');

      if (drawZAxis) {

        // always constant, z-axis is not a relative scale
        const graphTicksZ = [-4, -3, -2, -1, 0, 1, 2, 3];
        // 3.9840000000000066 is the magic number we receive for x when mean =0, stdev = 1; hence the same here
        const domainZ = [-4, 3.9840000000000066];

        const z = d3
          .scaleLinear()
          .range([0, width])
          .domain(domainZ);

        let formatterZ = d3.axisBottom(z);
        if (graphTicks !== null) {
          formatterZ = d3.axisBottom(z).tickFormat(d3.format('.2f')).tickValues(graphTicksZ);
        }

        // Draw z-Axis with z-scale
        svg
          .select('#z-axis')
          .attr('class', 'z axis')
          .attr('transform', `translate(0, ${height + 20})`)
          .call(formatterZ);

        // Draw z-Axis label
        svg.append('text')
          .attr('class', 'z-label')
          .attr('transform', `translate( ${(width + 30)} , ${(height + 25)})`)
          .style('text-anchor', 'end')
          .text('z');
      }

      // remove old paths
      svg.selectAll('.path').remove();

      // draw new paths
      each(this.props.paths, path => {
        svg
          .append('path')
          .datum(
            path.className === 'orange'
              ? [
                { ...first(path.points), y: ranges.y.min },
                ...path.points,
                { ...last(path.points), y: ranges.y.min }
              ]
              : path.points
          )
          .attr('class', `path ${path.className}`)
          .attr('d', line);
      });

      // remove old dots
      svg.selectAll('.node').remove();

      // draw new dots

      // Prevent building nodes outside the ranges
      const nodesToShow = this.props.nodes.filter(node => node.x >= ranges.x.min && node.x <= ranges.x.max);

      // when the 'both tails' option is chosen here we need to check whether form is valid to hide dots when they are intersected
      !isInvalidForm &&
        svg.selectAll('.node')
          .data(nodesToShow)
          .enter()
          .append('circle')
          .attr('class', 'node')
          .attr('cx', d => x(d.x))
          .attr('cy', d => y(d.y))
          .attr('r', 5);
    };

    this.componentDidMount = this.createLineChart;
    this.componentDidUpdate = this.createLineChart;

    this.saveSvgNode = node => {
      this.node = node;
    };
  }

  render () {
    return (
      <svg
        ref={this.saveSvgNode}
        width={this.props.canvasWidth}
        height={this.props.canvasHeight}
      >

        <g id="container">
          <g id="y-axis" />
          <g id="x-axis" />
          {this.props.drawZAxis && <g id="z-axis" />}
        </g>
      </svg>
    );
  }
}

LineChartComponent.propTypes = {
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired
    }).isRequired
  ).isRequired,

  paths: PropTypes.arrayOf(
    PropTypes.shape({
      points: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired
        }).isRequired
      ).isRequired,

      className: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,

  ranges: PropTypes.shape({
    x: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired
    }).isRequired,

    y: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired
    }).isRequired
  }).isRequired,
  graphTicks: PropTypes.array,
  canvasWidth: PropTypes.number.isRequired,
  canvasHeight: PropTypes.number.isRequired,
  drawZAxis: PropTypes.bool,
  isInvalidForm: PropTypes.bool.isRequired
};

export default LineChartComponent;
